import * as React from 'react';
import type { Meta, StoryObj } from '@storybook/react';
import { unstable_GridLayout as GridLayout } from '../GridLayout';
import { Card } from '../Card';

/**
 * Create layouts based on CSS Grid.
 *
 * This is an unstable component, expect breaking changes or complete removal in minor versions.
 *
 * **Usage**
 *
 * ```jsx
 * import { unstable_GridLayout as GridLayout } from '../GridLayout';
 *
 * // ...
 *
 * <GridLayout>
 *   <GridLayout.Item mdSpan={6} lgSpan={4}>...</GridLayout.Item>
 * </GridLayout>
 * ```
 */
const meta: Meta<typeof GridLayout> = {
  title: 'Unstable/GridLayout',
  component: GridLayout,
  subcomponents: {
    'GridLayout.Item': GridLayout.Item,
  },
};

export default meta;
type Story = StoryObj<typeof GridLayout>;

export const Default: Story = {
  render(props) {
    return (
      <GridLayout {...props}>
        <GridLayout.Item style={{ background: 'pink' }} smSpan={12} mdSpan={4} lgSpan={4}>
          <p style={{ padding: 8 }}>Foo</p>
        </GridLayout.Item>
        <GridLayout.Item style={{ background: 'aliceblue' }} smSpan={12} mdSpan={4} lgSpan={4}>
          <p style={{ padding: 8 }}>Bar</p>
        </GridLayout.Item>
        <GridLayout.Item style={{ background: 'lavenderblush' }} smSpan={12} mdSpan={4} lgSpan={4}>
          <p style={{ padding: 8 }}>Baz</p>
        </GridLayout.Item>
      </GridLayout>
    );
  },
};

/** Two column layout which wraps at `sm` viewport. */
export const TwoColumns: Story = {
  render(props) {
    return (
      <GridLayout {...props}>
        <GridLayout.Item smSpan={12} mdSpan={6}>
          <Card header="Foo">
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis porro debitis possimus voluptate
              doloribus odit neque quae quam? Fugit, cumque.
            </p>
          </Card>
        </GridLayout.Item>
        <GridLayout.Item smSpan={12} mdSpan={6}>
          <Card header="Bar">
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis porro debitis possimus voluptate
              doloribus odit neque quae quam? Fugit, cumque.
            </p>
          </Card>
        </GridLayout.Item>
        <GridLayout.Item smSpan={12} mdSpan={6}>
          <Card header="Baz">
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis porro debitis possimus voluptate
              doloribus odit neque quae quam? Fugit, cumque.
            </p>
          </Card>
        </GridLayout.Item>
        <GridLayout.Item smSpan={12} mdSpan={6}>
          <Card header="Baz">
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis porro debitis possimus voluptate
              doloribus odit neque quae quam? Fugit, cumque.
            </p>
          </Card>
        </GridLayout.Item>
      </GridLayout>
    );
  },
};

/** Three column layout which wraps to two columns at `md` viewport, and one column at `sm` viewport. */
export const ThreeColumns: Story = {
  render(props) {
    return (
      <GridLayout {...props}>
        <GridLayout.Item smSpan={12} mdSpan={6} lgSpan={4}>
          <Card header="Foo">
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis porro debitis possimus voluptate
              doloribus odit neque quae quam? Fugit, cumque.
            </p>
          </Card>
        </GridLayout.Item>
        <GridLayout.Item smSpan={12} mdSpan={6} lgSpan={4}>
          <Card header="Bar">
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis porro debitis possimus voluptate
              doloribus odit neque quae quam? Fugit, cumque.
            </p>
          </Card>
        </GridLayout.Item>
        <GridLayout.Item smSpan={12} mdSpan={6} lgSpan={4}>
          <Card header="Baz">
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis porro debitis possimus voluptate
              doloribus odit neque quae quam? Fugit, cumque.
            </p>
          </Card>
        </GridLayout.Item>
      </GridLayout>
    );
  },
};

/** Adjust starting column with `start` prop to create an offset between columns. */
export const StartColumn: Story = {
  render(props) {
    return (
      <GridLayout {...props}>
        <GridLayout.Item style={{ background: 'pink' }} mdSpan={4}>
          <p style={{ padding: 8 }}>mdSpan=4</p>
        </GridLayout.Item>
        <GridLayout.Item style={{ background: 'aliceblue' }} mdSpan={4} mdStart={6}>
          <p style={{ padding: 8 }}>mdSpan=4, mdStart=6</p>
        </GridLayout.Item>
        <GridLayout.Item style={{ background: 'lavenderblush' }} mdSpan={2} mdStart={11}>
          <p style={{ padding: 8 }}>mdSpan=2, mdStart=11</p>
        </GridLayout.Item>
      </GridLayout>
    );
  },
};
